/** biome-ignore-all lint/complexity/noStaticOnlyClass: Stateless API wrapper, static methods are sufficient. */
import { reportError } from '@newo/error-reporting';
import { jwtDecode } from 'jwt-decode';
import { z } from 'zod';
import { apiClient } from '../../ApiClient';
import { ApiClientErrors } from '../../types';
import { createAuthHeaders } from './auth-headers';
import type {
	AuthHeaders,
	AuthRequestConfig,
	AuthSessionTokenPair,
	AuthTokenLoginUrlRequest,
	AuthTokenPairRequest,
	AuthTokenPairResponse,
	AuthTokenPayload,
	AuthUtilRequestConfig,
	CustomerTokenRequest,
	ProvisioningTokenRequest,
	ProvisioningTokenResponse,
	SsoTokenRequest
} from './types';

const OutboundSsoLaunchResponseSchema = z.object({
	redirect_url: z
		.string()
		.url()
		.refine(
			(redirectUrl) => new URL(redirectUrl).protocol === 'https:',
			'Outbound SSO redirect URL must use HTTPS'
		)
});

export type OutboundSsoLaunchResponse = z.infer<
	typeof OutboundSsoLaunchResponseSchema
>;

export class AuthService {
	static createAuthHeaders(accessToken: string): AuthHeaders {
		return createAuthHeaders(accessToken);
	}

	static createTokenLoginUrl({
		baseUrl,
		tokens
	}: AuthTokenLoginUrlRequest): string {
		const params = new URLSearchParams({
			access_token: tokens.access_token,
			refresh_token: tokens.refresh_token,
			expires_in: String(tokens.expires_in)
		});

		return `${baseUrl}/auth/token-login?${params}`;
	}

	static getTokenPair(
		data: AuthTokenPairRequest,
		config?: AuthRequestConfig
	): Promise<AuthTokenPairResponse> {
		return apiClient.post('v1/auth/auth0/token', data, {
			...config,
			errorType: ApiClientErrors.GetTokenPair
		});
	}

	static generateCustomerToken(
		data: CustomerTokenRequest,
		config?: AuthUtilRequestConfig
	): Promise<AuthTokenPairResponse> {
		return apiClient.post('v1/auth/generate-customer-token', data, {
			...config,
			errorType: ApiClientErrors.GenerateCustomerToken
		});
	}

	static getSsoToken(
		request: SsoTokenRequest,
		config?: AuthUtilRequestConfig
	): Promise<AuthTokenPairResponse> {
		const params = new URLSearchParams({
			state: request.state,
			tenant: request.tenant
		});

		return apiClient.get(
			`v1/oauth/${request.code}/token?${params.toString()}`,
			{
				...config,
				errorType: ApiClientErrors.GetSsoToken
			}
		);
	}

	static launchOutboundSso(): Promise<OutboundSsoLaunchResponse> {
		return apiClient
			.post('v1/sso/outbound/launch', undefined, {
				errorType: ApiClientErrors.LaunchOutboundSso,
				noErrorMessage: true
			})
			.then((response) => OutboundSsoLaunchResponseSchema.parse(response));
	}

	static getProvisioningToken(
		request: ProvisioningTokenRequest
	): Promise<ProvisioningTokenResponse> {
		return apiClient.get(
			`${request.provisioningUrl}/provisioning/auth/${request.code}/token`,
			{
				errorType: ApiClientErrors.GetProvisioningToken,
				headers: {
					'x-portal-secret': request.portalSecret
				},
				returnFullResponse: true,
				skipAuth: true
			}
		);
	}

	static setTokens(
		tokens: AuthSessionTokenPair | AuthTokenPairResponse,
		expiresIn?: number | string
	): void {
		if ('access_token' in tokens) {
			apiClient.setTokens(
				{
					accessToken: tokens.access_token,
					refreshToken: tokens.refresh_token
				},
				tokens.expires_in
			);
			return;
		}

		apiClient.setTokens(tokens, expiresIn);
	}

	static clearTokens(): void {
		apiClient.clearTokens();
	}

	static getTokens(): AuthSessionTokenPair | null {
		return apiClient.getTokens();
	}

	static getRemainingExpiresIn(): number | null {
		return apiClient.getRemainingExpiresIn();
	}

	static hasSession(): boolean {
		return AuthService.getTokens() !== null;
	}

	static getCurrentCustomerId(): string | null {
		const accessToken = AuthService.getTokens()?.accessToken;

		if (!accessToken) {
			return null;
		}

		try {
			return jwtDecode<AuthTokenPayload>(accessToken).customer_id ?? null;
		} catch (error) {
			reportError(error, { message: 'Auth token decode failed' });
			return null;
		}
	}
}
